// ©2024 Austin App House. All rights reserved.
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toggleModal } from '../../storage/actions/modalsActions';
import { reloadData } from '../../storage/actions/applicationStateActions';
import DashboardIcon from '../dashboard/icons/DashboardIcon';
import Loader from '../dashboard/common/Loader';
import Notification from '../notification';
import {
  fetchCompanyUsers,
  changeCompanyAccountOwner,
  getCompanyId,
} from '../../utils/api/adminCompanies';

const isOwner = (user, company) => {
  if (user.isAccountOwner) return true;
  const ownerEmail = company?.creatorEmail;
  return Boolean(ownerEmail) && user.email === ownerEmail;
};

const CompanyUsersModal = ({
  modalState, toggleModal, reloadData: reloadAppData, isReload,
}) => {
  const company = modalState?.type?.company || {};
  const companyId = getCompanyId(company);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      setUsers(await fetchCompanyUsers(companyId));
    } catch (error) {
      Notification('error', {
        message: error.response?.data?.message || error.message,
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (modalState?.isOpen) loadUsers();
  }, [modalState?.isOpen, isReload, loadUsers]);

  const close = () => toggleModal(false, 'companyUsersModal');

  const handleEdit = (user) => {
    toggleModal(true, 'addOrEditUsersForManagement', true, { ...user, companyId });
  };

  const handleDelete = (user) => {
    toggleModal(true, 'deleteClientFromCompany', true, {
      client: {
        ...user,
        firstname: user.firstName,
        lastname: user.lastName,
        userType: user.role,
        number: user.phone,
        companyId,
      },
    });
  };

  const handleChangeOwner = (currentOwner) => {
    toggleModal(true, 'changeAccountOwnerModal', true, {
      users,
      currentOwnerId: currentOwner._id || currentOwner.id,
      onSubmit: async (ownerUserId) => {
        try {
          const result = await changeCompanyAccountOwner(companyId, ownerUserId);
          Notification('success', {
            message: result?.message || 'Account owner updated.',
          });
          reloadAppData();
        } catch (error) {
          Notification('error', {
            message: error.response?.data?.message || error.message,
          });
          throw error;
        }
      },
    });
  };

  if (!modalState?.isOpen) return null;

  return (
    <div className="popup_block dashboard-modal showed">
      <div className="popup_container popup_container--wide">
        <div className="popup_head">
          <div className="popup_title">
            {company.name ? `${company.name} — Users` : 'Company Users'}
          </div>
          <button type="button" className="close_btn" aria-label="close" onClick={close}>
            ×
          </button>
        </div>

        <div className="company-users-meta">
          <span className="company-users-meta__label">Created by</span>
          <span className="company-users-meta__value">
            {company.creatorName || '—'}
            {company.creatorEmail ? ` (${company.creatorEmail})` : ''}
          </span>
        </div>

        <div className="subpage-table-scroll">
          {loading ? (
            <div className="subpage-loading">
              <Loader />
              <span>Loading users…</span>
            </div>
          ) : (
            <table className="subpage-table subpage-table--users">
              <thead>
                <tr>
                  <th>First name</th>
                  <th>Last name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={7}>No users found</td></tr>
                ) : users.map((user) => {
                  const owner = isOwner(user, company);
                  return (
                    <tr key={user._id || user.email}>
                      <td data-th="First name">{user.firstName}</td>
                      <td data-th="Last name">{user.lastName}</td>
                      <td data-th="Email">{user.email}</td>
                      <td data-th="Phone">{user.phone}</td>
                      <td data-th="Role">{user.role}</td>
                      <td data-th="Status">{user.status}</td>
                      <td className="actions-col">
                        <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="row-action-btn"
                            title="Edit"
                            aria-label="Edit user"
                            onClick={() => handleEdit(user)}
                          >
                            <DashboardIcon name="edit" size={14} strokeWidth="2" />
                          </button>
                          <button
                            type="button"
                            className="row-action-btn row-action-btn--danger"
                            title="Delete"
                            aria-label="Delete user"
                            onClick={() => handleDelete(user)}
                          >
                            <DashboardIcon name="trash" size={14} strokeWidth="2" />
                          </button>
                          {owner ? (
                            <button
                              type="button"
                              className="row-action-btn row-action-btn--owner"
                              data-tooltip="Account Owner"
                              aria-label="Change account owner"
                              onClick={() => handleChangeOwner(user)}
                            >
                              <DashboardIcon name="star" size={14} strokeWidth="2" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  modalState: state.modalState.companyUsersModal,
  isReload: state.applicationState.isReload,
});

export default connect(mapStateToProps, { toggleModal, reloadData })(CompanyUsersModal);
